import { MouseEvent } from "react";
import { IconProps } from "@chakra-ui/react";
import { Box, BoxProps, Chip, List, ListItem } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { SpanBox } from "src/components/base";
import { IconCaret } from "src/components/icons";
import { CLASS_NAV_ICON_CARET, CLASS_NAV_UNDERLINE } from "src/constants/dom";
import { INav, INavLink } from "src/types/layout";
import { createMiddleClickHandler } from "src/utils/dom/mouse/mouse";
import { getPathnameFromRouter } from "src/utils/url/url";
import { MenuLink } from "./menu-link";
import { SubMenuContainer, SubMenuLink } from "./sub-menu";
import { useDesktopNav } from "./use-desktop-menu";
import { useNavigationTracking } from "./use-navigation-tracking";
import { useSearchViewModeLink } from "./use-search-view-mode-link";

interface IActiveIndicatorProps {
    item: INavLink;
    currentPath: string;
}

const ActiveIndicator = ({ item, currentPath }: IActiveIndicatorProps) => {
    const isSelfOrChildActive = () => {
        if (item.link.url === currentPath) {
            return true;
        }

        return item.children?.some(child => child.link.url === currentPath);
    };

    return (
        <SpanBox
            pos="absolute"
            bottom={0}
            w="full"
            h="2px"
            rounded="1.5px"
            bg="current"
            opacity={isSelfOrChildActive() ? 1 : 0}
            transition="opacity 0.2s"
            className={CLASS_NAV_UNDERLINE}
        />
    );
};

interface IProps extends Pick<BoxProps, "sx"> {
    nav: INav;
    isSearchOpen: boolean;
}

export const MenuDesktop = ({ nav, isSearchOpen, sx: boxSx }: IProps) => {
    const { t } = useTranslation("common");
    const path = getPathnameFromRouter(useRouter());
    const { sx, handleSubMenuItemClick } = useDesktopNav();
    const { trackMainNavClick, trackSubNavClick } = useNavigationTracking();
    const { handleSearchLinkClick } = useSearchViewModeLink();

    const onSubMenuLinkClicked = (event: MouseEvent<HTMLElement, globalThis.MouseEvent>, url: string) => {
        handleSearchLinkClick(event, url);
        trackSubNavClick(url);
        handleSubMenuItemClick(event);
    };

    if (isSearchOpen) {
        return null;
    }

    return (
        <Box component="nav" sx={{ flexGrow: 1, ...boxSx }}>
            <List sx={{ display: "flex", p: 0 }} role="menubar">
                {nav.main.map(item => (
                    <ListItem
                        key={item.link.url}
                        disablePadding
                        role="none"
                        sx={{ position: "relative", width: "auto", whiteSpace: "nowrap", ...sx }}
                    >
                        <MenuLink
                            onClick={event => {
                                handleSearchLinkClick(event, item.link.url);
                                trackMainNavClick(item.link.url);
                            }}
                            onAuxClick={createMiddleClickHandler(() => trackMainNavClick(item.link.url))}
                            target={item.link.target}
                            href={item.link.url}
                            hasChildElements={item.children.length > 0}
                        >
                            {item.link.name}
                            <ActiveIndicator item={item} currentPath={path} />
                        </MenuLink>
                        {item.children.length > 0 && (
                            <SubMenuContainer align="left">
                                {item.children.map(({ link, isNew }) => (
                                    <SubMenuLink
                                        key={link.url}
                                        href={link.url}
                                        target={link.target}
                                        isActive={link.url === path}
                                        onClick={event => {
                                            onSubMenuLinkClicked(event, link.url);
                                        }}
                                        onAuxClick={createMiddleClickHandler(event => onSubMenuLinkClicked(event, link.url))}
                                    >
                                        {link.name}
                                        {isNew && <Chip label={t("new")} color="green" size="medium" sx={{ ml: 1 }} />}
                                    </SubMenuLink>
                                ))}
                            </SubMenuContainer>
                        )}
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

export const NavIconCaret = (props: Omit<IconProps, "css" | "children">) => (
    <IconCaret boxSize={2} transition="transform 0.2s" className={CLASS_NAV_ICON_CARET} {...props} />
);
